import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BadgeCheck, Mail, RefreshCcw, Play } from "lucide-react";
import { toast } from "sonner";

const chf = (v) => new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }).format(Number(v || 0));
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const fmtPeriod = (p) => (p ? `${MONTHS[Number(p.slice(5, 7)) - 1]} ${p.slice(0, 4)}` : "—");
const STATUS = {
  pending: ["Prüfung fällig", "bg-amber-100 text-amber-800"],
  approved: ["freigegeben", "bg-emerald-100 text-emerald-800"],
  corrected: ["korrigiert & freigegeben", "bg-emerald-100 text-emerald-800"],
  rejected: ["abgelehnt", "bg-slate-200 text-slate-700"],
};

/** Iter310 (F2-40…63): Bonus-Vorgänge – Vorschlag nachvollziehbar, Freigabe / Korrektur / Ablehnung, E-Mail-Retry. */
export function BonusCasesCard({ companyId, onChanged, highlightId }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/bonus-cases`);
      setItems(r.data || []);
    } catch (e) { console.error(e); }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action, body = {}) => {
    setBusy(id + action);
    try {
      const r = await apiClient.post(`/companies/${companyId}/bonus-cases/${id}/${action}`, body);
      if (action === "remind") toast[r.data?.sent ? "success" : "error"](r.data?.sent ? `E-Mail an ${r.data.to} gesendet` : r.data?.error || "E-Mail fehlgeschlagen");
      else toast.success(action === "reject" ? "Bonus abgelehnt" : `Bonus ${chf(r.data?.final_amount)} freigegeben → ${r.data?.payroll?.action || "Lohnlauf"}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally { setBusy(""); }
  };
  const runCheck = async () => {
    setBusy("check");
    try {
      const r = await apiClient.post(`/companies/${companyId}/bonus-cases/run-check`);
      toast.success(`${r.data.created} neue Bonusprüfung(en), ${r.data.existing} bereits vorhanden, ${r.data.notified} E-Mails gesendet`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Prüfung fehlgeschlagen"); } finally { setBusy(""); }
  };

  if (items.length === 0) return null;
  return (
    <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="bonus-cases-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BadgeCheck className="h-4 w-4 text-emerald-600" />
          <h3 className="text-sm font-semibold text-slate-800">Bonusprüfung &amp; Freigabe</h3>
          <span className="text-xs text-slate-400">Stichtag je Bonusmodell · Freigabe vor Übernahme in den Lohnlauf</span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runCheck} disabled={busy === "check"} data-testid="bonus-run-check-button"><Play className="h-3.5 w-3.5 mr-1" />Stichtag prüfen</Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="bonus-cases-refresh"><RefreshCcw className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      <Table data-testid="bonus-cases-table">
        <TableHeader><TableRow>
          <TableHead>Periode</TableHead><TableHead>Mitarbeiter</TableHead><TableHead>Berechnung</TableHead>
          <TableHead className="text-right">Vorschlag</TableHead><TableHead>Status</TableHead><TableHead>E-Mail</TableHead><TableHead className="text-right">Aktion</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {items.map((c) => {
            const [label, cls] = STATUS[c.status] || STATUS.pending;
            const ed = edit[c.id] || {};
            return (
              <TableRow key={c.id} className={highlightId === c.id ? "bg-amber-50" : ""} data-testid={`bonus-case-${c.id}`}>
                <TableCell className="text-sm">{fmtPeriod(c.period)}</TableCell>
                <TableCell className="text-sm">{c.employee_name}</TableCell>
                <TableCell className="text-xs text-slate-600">
                  <div>Grundlage {chf(c.base_amount)} − Abzüge {chf(c.deductions)} = Basis {chf(c.bonus_base)}</div>
                  <div>{c.rule === "percent" ? `× ${c.percent} %` : "Fixbetrag"} = <b>{chf(c.proposed_amount)}</b></div>
                  {c.final_amount != null && c.final_amount !== c.proposed_amount && <div className="text-amber-700">korrigiert auf {chf(c.final_amount)}</div>}
                </TableCell>
                <TableCell className="text-right text-sm font-semibold">{chf(c.final_amount ?? c.proposed_amount)}</TableCell>
                <TableCell><Badge className={cls} data-testid={`bonus-case-status-${c.id}`}>{label}</Badge></TableCell>
                <TableCell className="text-xs">
                  <span className={c.notification?.sent ? "text-emerald-700" : "text-rose-600"} title={c.notification?.error || c.notification?.to || ""}>
                    <Mail className="inline h-3.5 w-3.5 mr-1" />{c.notification?.sent ? "gesendet" : "fehlgeschlagen"}
                  </span>
                  {!c.notification?.sent && <button className="ml-1 underline text-slate-500" onClick={() => act(c.id, "remind")} data-testid={`bonus-remind-${c.id}`}>erneut</button>}
                </TableCell>
                <TableCell className="text-right">
                  {c.status === "pending" ? (
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex gap-1">
                        <Input className="h-7 w-24 text-right text-xs" type="number" step="0.05" placeholder={String(c.proposed_amount)} value={ed.amount ?? ""}
                          onChange={(e) => setEdit({ ...edit, [c.id]: { ...ed, amount: e.target.value } })} data-testid={`bonus-amount-${c.id}`} />
                        <Input className="h-7 w-40 text-xs" placeholder="Begründung (bei Korrektur)" value={ed.reason ?? ""}
                          onChange={(e) => setEdit({ ...edit, [c.id]: { ...ed, reason: e.target.value } })} data-testid={`bonus-reason-${c.id}`} />
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" className="h-7 bg-emerald-700 text-white hover:bg-emerald-800" disabled={!!busy}
                          onClick={() => act(c.id, "approve", { amount: ed.amount ? Number(ed.amount) : null, reason: ed.reason || null })} data-testid={`bonus-approve-${c.id}`}>Freigeben</Button>
                        <Button size="sm" variant="outline" className="h-7" disabled={!!busy}
                          onClick={() => act(c.id, "reject", { reason: ed.reason || null })} data-testid={`bonus-reject-${c.id}`}>Ablehnen</Button>
                      </div>
                    </div>
                  ) : <span className="text-xs text-slate-500">{c.approved_by || c.rejected_by || ""}</span>}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}
